import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import MobilePageBar, { NewPostButton } from '../components/MobilePageBar';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface DraftRecord {
  id: string;
  platform: string;
  content_type: string;
  status: string;
}

interface DraftPost {
  id: string;
  video_id: string;
  short_id: string;
  short_thumbnail_url: string | null;
  short_file_url: string | null;
  caption: string | null;
  hashtags: string[];
  schedule_type: string;
  scheduled_time: string | null;
  timezone: string;
  overall_status: string;
  created_at: string;
  updated_at: string;
  project_id: string | null;
  project_name: string | null;
  title: string | null;
  records: DraftRecord[];
}

interface DraftsResponse {
  count: number;
  drafts: DraftPost[];
}

function formatDate(value: string | null): string {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
}

function resolveMediaUrl(value: string | null): string | null {
  if (!value) {
    return null;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  return `${API_BASE}${value}`;
}

const PostsDrafts: React.FC = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<DraftPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [draftPendingDelete, setDraftPendingDelete] = useState<DraftPost | null>(null);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/posts/drafts`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload.error || 'Failed to load drafts');
      }

      const payload = await response.json() as DraftsResponse;
      setDrafts(Array.isArray(payload.drafts) ? payload.drafts : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load drafts');
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDrafts();
  }, [loadDrafts]);

  const handleDelete = useCallback(async () => {
    if (!draftPendingDelete) {
      return;
    }

    setDeletingDraftId(draftPendingDelete.id);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/posts/drafts/${draftPendingDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload.error || 'Failed to delete draft');
      }

      setDrafts(prev => prev.filter(item => item.id !== draftPendingDelete.id));
      setDraftPendingDelete(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete draft');
    } finally {
      setDeletingDraftId(null);
    }
  }, [draftPendingDelete]);

  const requestDelete = useCallback((draft: DraftPost) => {
    setDraftPendingDelete(draft);
  }, []);

  const dismissDelete = useCallback(() => {
    if (deletingDraftId) {
      return;
    }
    setDraftPendingDelete(null);
  }, [deletingDraftId]);

  const handleEdit = useCallback((draft: DraftPost) => {
    const params = new URLSearchParams({
      draftId: draft.id,
    });

    if (draft.project_id) {
      params.set('projectId', draft.project_id);
    }

    if (draft.video_id) {
      params.set('videoId', draft.video_id);
    }

    navigate(`/posts/compose?${params.toString()}`);
  }, [navigate]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <MobilePageBar current="Drafts" label="📝 Drafts">
          <NewPostButton />
        </MobilePageBar>

        <div className="page-header posts-list-header">
          <div>
            <h1>Drafts</h1>
            <p className="page-subtitle">Posts saved as drafts</p>
          </div>
        </div>

        {error && (
          <div className="error-banner" style={{ marginBottom: 16 }}>
            <span>{error}</span>
            <button className="btn-secondary" onClick={() => void loadDrafts()}>Retry</button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading drafts...
          </div>
        ) : drafts.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
            <p>No draft posts yet.</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Compose a new post and save it as a draft.</p>
          </div>
        ) : (
          <div className="posts-drafts-list">
            {drafts.map(draft => (
              <article key={draft.id} className="posts-draft-card">
                <div className="posts-draft-thumbnail">
                  {resolveMediaUrl(draft.short_thumbnail_url) ? (
                    <img
                      src={resolveMediaUrl(draft.short_thumbnail_url) || ''}
                      alt={draft.title || 'Draft thumbnail'}
                    />
                  ) : resolveMediaUrl(draft.short_file_url) ? (
                    <video
                      src={resolveMediaUrl(draft.short_file_url) || ''}
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <div className="posts-draft-thumbnail-fallback">No Preview</div>
                  )}
                </div>

                <div className="posts-draft-main">
                  <div className="posts-draft-header-row">
                    <div>
                      <h3>{draft.title || 'Untitled Draft'}</h3>
                      <p className="posts-draft-project">{draft.project_name || 'No Project'}</p>
                    </div>
                    <span className="status-badge">{draft.overall_status}</span>
                  </div>

                  <p className="posts-draft-caption">{draft.caption || 'No caption yet'}</p>

                  {draft.hashtags.length > 0 && (
                    <div className="posts-draft-tags">
                      {draft.hashtags.slice(0, 6).map(tag => (
                        <span key={`${draft.id}-${tag}`} className="status-pill posts-draft-tag-pill">
                          #{tag}
                        </span>
                      ))}
                      {draft.hashtags.length > 6 && (
                        <span className="posts-draft-tags-more">+{draft.hashtags.length - 6} more</span>
                      )}
                    </div>
                  )}

                  <div className="posts-draft-meta-row">
                    <div>Platforms: {draft.records.map(record => record.platform).join(', ') || 'n/a'}</div>
                    <div>Updated: {formatDate(draft.updated_at)}</div>
                  </div>
                </div>

                <div className="posts-draft-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleEdit(draft)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-secondary posts-draft-delete-action"
                    onClick={() => requestDelete(draft)}
                    disabled={deletingDraftId === draft.id}
                  >
                    {deletingDraftId === draft.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {draftPendingDelete && (
          <div className="posts-draft-delete-overlay" onClick={dismissDelete} role="presentation">
            <div
              className="posts-draft-delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="posts-draft-delete-title"
              onClick={event => event.stopPropagation()}
            >
              <h3 id="posts-draft-delete-title">Delete Draft</h3>
              <p>
                Delete <strong>{draftPendingDelete.title || 'Untitled Draft'}</strong>? This action cannot be undone.
              </p>
              <div className="posts-draft-delete-actions">
                <button className="btn-secondary" onClick={dismissDelete} disabled={Boolean(deletingDraftId)}>
                  Cancel
                </button>
                <button
                  className="btn-primary posts-draft-delete-confirm"
                  onClick={() => void handleDelete()}
                  disabled={Boolean(deletingDraftId)}
                >
                  {deletingDraftId ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PostsDrafts;
